/**
 * PATH: apps/qa/src/pages/area-execution/CompletionSection.tsx
 * PURPOSE: Area completion banner with upload, sign-off, and lock actions
 */

import { CheckCircle2, Upload, Camera, PenLine, Lock, Paperclip } from 'lucide-react'
import type { ItemInstance, Evidence } from '@/types/qa'

interface CompletionSectionProps {
  items: ItemInstance[]
  evidence: Evidence[]
  isUploadingDoc: boolean
  onUploadFiles: () => void
  onTakePhoto: () => void
  onRequestSignOff: () => void
  onLockArea: () => void
}

export function CompletionSection({
  items, evidence, isUploadingDoc, onUploadFiles, onTakePhoto, onRequestSignOff, onLockArea,
}: CompletionSectionProps) {
  return (
    <div className="mt-6 space-y-3 animate-in slide-in-from-bottom-4 duration-500">
      <div className="p-4 rounded-xl border-2 border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">All Items Complete</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {items.filter(i => i.status === 'done').length} passed, {items.filter(i => i.status === 'failed').length} failed, {items.filter(i => i.status === 'not_applicable').length} N/A
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={onUploadFiles} disabled={isUploadingDoc}
          className="p-4 rounded-xl border border-border bg-card hover:bg-accent transition-all text-left">
          <Upload className="w-5 h-5 text-blue-500 mb-2" />
          <p className="font-medium text-foreground text-sm">Upload Files</p>
          <p className="text-xs text-muted-foreground">Photos, PDFs, drawings</p>
        </button>
        <button onClick={onTakePhoto}
          className="p-4 rounded-xl border border-border bg-card hover:bg-accent transition-all text-left">
          <Camera className="w-5 h-5 text-teal-500 mb-2" />
          <p className="font-medium text-foreground text-sm">Take Photo</p>
          <p className="text-xs text-muted-foreground">Capture evidence</p>
        </button>
      </div>

      {evidence.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground px-1">
          <Paperclip className="w-3.5 h-3.5" />
          {evidence.length} file{evidence.length !== 1 ? 's' : ''} attached to this area
        </div>
      )}

      <button onClick={onRequestSignOff}
        className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm">
        <PenLine className="w-5 h-5" />Request Area Sign-off
      </button>

      <button onClick={onLockArea}
        className="w-full py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm">
        <Lock className="w-5 h-5" />Lock Area
      </button>
    </div>
  )
}
